import React from 'react';
import { Helmet } from 'react-helmet-async';
import { GuideSpokePageView } from 'alakai-aloha-shared/public-ui';
import TripPlannerPromoHero from '../../components/shared/TripPlannerPromoHero';
import { urlFor } from '../../lib/sanityImage';
import { PortableText } from '@portabletext/react';
import { buildRuntimeCanonicalUrl, getRuntimePublicBaseUrl } from '../../lib/siteUrls';

const portableTextComponents = {
  marks: {
    link: ({ value, children }) => {
      const href = typeof value?.href === 'string' ? value.href : '#';
      const external = href.startsWith('http');
      return (
        <a href={href} className="text-emerald-400 underline hover:text-emerald-300 transition-colors" {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {children}
        </a>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl font-extrabold tracking-tight mt-12 mb-6">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl font-bold mt-10 mb-4 border-b border-white/20 pb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-semibold mt-6 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg font-medium italic mt-4 mb-1">{children}</h4>,
    normal: ({ children }) => <p className="mb-4">{children}</p>,
    blockquote: ({ children }) => <blockquote className="italic border-l-4 border-emerald-400 pl-4 my-6">{children}</blockquote>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
};

function getImageUrl(image, width = 900) {
  if (!image?.asset) return undefined;
  return urlFor(image).width(width).fit('max').auto('format').url();
}

function getEntityImage(ref) {
  if (!ref) return {};
  if (ref.mainImage?.asset) return { url: getImageUrl(ref.mainImage), alt: ref.mainImage.alt };
  if (ref.images?.[0]?.asset) return { url: getImageUrl(ref.images[0]), alt: ref.images[0].alt };
  if (ref.heroImage?.asset) return { url: getImageUrl(ref.heroImage), alt: ref.heroImage.alt };
  return {};
}

function mapEntity(item) {
  const ref = item?.ref;
  if (!ref) return null;
  const slug = typeof ref.slug === 'string' ? ref.slug : ref.slug?.current;
  const image = getEntityImage(ref);
  return {
    id: ref._id,
    type: ref._type,
    slug,
    name: ref.name || ref.title,
    area: ref.area,
    shortDescription: ref.shortDescription,
    imageUrl: image.url,
    imageAlt: image.alt || ref.name || ref.title,
    headline: item.headline,
  };
}

function mapRelatedGuide(guide) {
  return {
    id: guide._id,
    title: guide.title,
    slug: typeof guide.slug === 'string' ? guide.slug : guide.slug?.current,
    previewImages: (guide.previewItems || []).map((item) => {
      const ref = item?.ref;
      if (!ref) return null;
      if (ref.mainImage?.asset) return { url: getImageUrl(ref.mainImage), alt: ref.mainImage.alt || ref.name || ref.title };
      if (ref.images?.[0]?.asset) return { url: getImageUrl(ref.images[0]), alt: ref.images[0].alt || ref.name || ref.title };
      if (ref.heroImage?.asset) return { url: getImageUrl(ref.heroImage), alt: ref.heroImage.alt || ref.name || ref.title };
      return null;
    }).filter(Boolean),
  };
}

export default function SpokePageLayout({ guide }) {
  const siteUrl = getRuntimePublicBaseUrl();
  const canonicalUrl = buildRuntimeCanonicalUrl(`/guides/${guide.slug}`);
  const seoTitle = guide.seo?.title || (guide.primaryKeyword ? `${guide.primaryKeyword}: ${guide.title} | Alaka'i Aloha` : `${guide.title} | Alaka'i Aloha`);
  const seoDescription = guide.seo?.description || '';
  const heroImage = guide.heroImage?.asset?.url;
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Guides', item: `${siteUrl}/guides` },
  ];

  if (guide.hubPage?.slug) {
    const hubSlug = typeof guide.hubPage.slug === 'string' ? guide.hubPage.slug : guide.hubPage.slug?.current;
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: guide.hubPage.title,
      item: `${siteUrl}/guides/${hubSlug}`,
    });
  }

  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbItems.length + 1,
    name: guide.title,
    item: canonicalUrl,
  });

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: guide.title,
    description: seoDescription || guide.primaryKeyword || guide.title,
    url: canonicalUrl,
    inLanguage: 'en-US',
    ...(heroImage ? { primaryImageOfPage: heroImage } : {}),
    ...(guide.lastReviewed ? { dateModified: guide.lastReviewed } : {}),
  };

  const viewGuide = {
    title: guide.title,
    slug: guide.slug,
    pageType: guide.pageType,
    heroImageUrl: heroImage,
    heroImageAlt: guide.heroImage?.alt || guide.title,
    introContent: guide.intro?.length ? <PortableText value={guide.intro} components={portableTextComponents} /> : null,
    hubPage: guide.hubPage?.title && guide.hubPage?.slug
      ? {
          title: guide.hubPage.title,
          slug: typeof guide.hubPage.slug === 'string' ? guide.hubPage.slug : guide.hubPage.slug?.current,
        }
      : undefined,
    siblingSpokes: (guide.siblingSpokes || []).map((spoke) => ({
      id: spoke._id,
      title: spoke.title,
      slug: typeof spoke.slug === 'string' ? spoke.slug : spoke.slug?.current,
    })),
    items: (guide.items || []).map(mapEntity).filter(Boolean),
    relatedGuides: (guide.relatedGuides || []).map(mapRelatedGuide),
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription && <meta name="description" content={seoDescription} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        {seoDescription && <meta property="og:description" content={seoDescription} />}
        <meta property="og:site_name" content="Alaka'i Aloha" />
        {heroImage && <meta property="og:image" content={heroImage} />}
        {guide.heroImage?.alt && <meta property="og:image:alt" content={guide.heroImage.alt} />}
        <meta name="twitter:card" content={heroImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={seoTitle} />
        {seoDescription && <meta name="twitter:description" content={seoDescription} />}
        {heroImage && <meta name="twitter:image" content={heroImage} />}
        {guide.seo?.noindex && <meta name="robots" content="noindex" />}
        <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <GuideSpokePageView guide={viewGuide} promoSlot={<TripPlannerPromoHero pageType="guide" />} />
    </>
  );
}
